import { Vec2 } from '../math/Vec2';
import { PADDLE } from '../config/constants';

export interface PaddleOptions {
  id: string;
  isHuman: boolean;
  color: string;
  isWall?: boolean;
  lives?: number;
  width?: number;
}

export class Paddle {
  id: string;
  isHuman: boolean;
  isWall: boolean;
  color: string;
  pos: number = 0.5; // Normalized position [0..1] along its arena side
  vel: number = 0;
  width: number;
  lives: number;

  tilt: number = 0; // Inward/outward tilt angle (radians)
  smashCharge: number = 0; // 0.0 to 1.0
  smashActiveTicks: number = 0;
  shieldTimer: number = 0;
  freezeTimer: number = 0;

  constructor(options: PaddleOptions) {
    this.id = options.id;
    this.isHuman = options.isHuman;
    this.color = options.color;
    this.isWall = options.isWall ?? false;
    this.lives = options.lives ?? (this.isWall ? 0 : PADDLE.INITIAL_LIVES);
    this.width = options.width ?? (this.isWall ? 1.0 : PADDLE.DEFAULT_WIDTH);
  }

  get isAlive(): boolean {
    return this.isWall || this.lives > 0;
  }

  triggerSmash(): boolean {
    if (this.smashCharge >= 1.0 && this.smashActiveTicks === 0 && !this.isWall) {
      this.smashCharge = 0;
      this.smashActiveTicks = PADDLE.SMASH_ACTIVE_TICKS;
      return true;
    }
    return false;
  }

  applyDamage(): { livesLeft: number; eliminated: boolean } {
    if (this.isWall) return { livesLeft: 0, eliminated: false };
    this.lives = Math.max(0, this.lives - 1);

    if (this.lives === 2) {
      this.width = PADDLE.WIDTH_2_LIVES;
    } else if (this.lives === 1) {
      this.width = PADDLE.WIDTH_1_LIFE;
    }

    return { livesLeft: this.lives, eliminated: this.lives === 0 };
  }

  heal(): boolean {
    if (this.isWall || this.lives >= PADDLE.INITIAL_LIVES) return false;
    this.lives++;
    if (this.lives === 3) {
      this.width = PADDLE.DEFAULT_WIDTH;
    } else if (this.lives === 2) {
      this.width = PADDLE.WIDTH_2_LIVES;
    }
    return true;
  }

  updatePhysics(dt: number, numSides: number, inputMove: number, inputTilt: number): void {
    if (this.isWall) return;

    // Timers decrement
    if (this.smashActiveTicks > 0) {
      this.smashActiveTicks--;
    } else if (this.smashCharge < 1.0) {
      this.smashCharge = Math.min(1.0, this.smashCharge + dt / PADDLE.SMASH_CHARGE_TIME_SEC);
    }

    if (this.shieldTimer > 0) this.shieldTimer--;
    if (this.freezeTimer > 0) this.freezeTimer--;

    // Human motion & tilt
    if (this.isHuman) {
      const speedScale = Math.sin(Math.PI / 8) / Math.sin(Math.PI / numSides);
      let accel = PADDLE.ACCEL_BASE * speedScale;
      let maxSpeed = PADDLE.MAX_SPEED_BASE * speedScale;

      if (this.freezeTimer > 0) {
        accel *= 0.4;
        maxSpeed *= 0.4;
      }

      if (inputMove !== 0) {
        this.vel += inputMove * accel;
      }
      this.vel *= PADDLE.FRICTION;
      this.vel = Math.max(-maxSpeed, Math.min(maxSpeed, this.vel));
      this.pos += this.vel;

      // Tilt
      if (inputTilt !== 0) {
        this.tilt += inputTilt * PADDLE.TILT_SPEED;
        this.tilt = Math.max(-PADDLE.MAX_TILT, Math.min(PADDLE.MAX_TILT, this.tilt));
      } else {
        this.tilt *= PADDLE.TILT_RECOVERY;
        if (Math.abs(this.tilt) < 0.005) this.tilt = 0;
      }
    }

    // Clamp paddle to edge bounds
    const halfWidth = this.width / 2;
    if (this.pos < halfWidth) {
      this.pos = halfWidth;
      this.vel = 0;
    } else if (this.pos > 1 - halfWidth) {
      this.pos = 1 - halfWidth;
      this.vel = 0;
    }
  }

  /**
   * Computes the exact physical endpoints and center of the paddle in world coordinates.
   */
  getPhysicalEndpoints(
    p1: Vec2,
    p2: Vec2,
    faceAngle: number
  ): { start: Vec2; end: Vec2; center: Vec2; normal: Vec2; isForwardSmash: boolean } {
    let thrust = 0;
    let isForwardSmash = false;

    if (this.smashActiveTicks > 0) {
      const progress = 1 - this.smashActiveTicks / PADDLE.SMASH_ACTIVE_TICKS;
      thrust = Math.sin(progress * Math.PI) * PADDLE.SMASH_THRUST_DISTANCE;
      isForwardSmash = progress <= 0.5;
    }

    const thrustX = -Math.cos(faceAngle) * thrust;
    const thrustY = -Math.sin(faceAngle) * thrust;

    const cx = p1.x + this.pos * (p2.x - p1.x) + thrustX;
    const cy = p1.y + this.pos * (p2.y - p1.y) + thrustY;
    const center = new Vec2(cx, cy);

    const edgeAngle = faceAngle + Math.PI / 2 + this.tilt;
    const sideLength = p1.distanceTo(p2);
    const paddleLength = this.width * sideLength;

    const dx = Math.cos(edgeAngle) * (paddleLength / 2);
    const dy = Math.sin(edgeAngle) * (paddleLength / 2);

    const start = new Vec2(cx - dx, cy - dy);
    const end = new Vec2(cx + dx, cy + dy);

    // Inward facing normal with tilt
    const inwardAngle = faceAngle + Math.PI + this.tilt;
    const normal = new Vec2(Math.cos(inwardAngle), Math.sin(inwardAngle));

    return { start, end, center, normal, isForwardSmash };
  }
}
